// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import debounce from 'lodash/debounce';
import type { State } from 'store';
import Circles from 'components/common/Loaders/Circles';
import OrderListTemplate from 'components/order/OrderListTemplate';
import { BaseActions, CartActions, OrdersActions } from '../../store/actionCreators';

type Props = {
  orders: any,
  loading: boolean,
};

class OrderListTemplateContainer extends Component<Props> {
  state = {
    page: 1,
    limit: 15,
    sort: '-created_at',
    q: '',
    hasMore: true,
    isSearching: false,
    items: [],
  };

  initialize = async () => {
    try {
      const { page, limit, sort, q } = this.state;
      const result = await OrdersActions.listOrder({ page, limit, sort, q });

      // API가 배열을 직접 반환하는 경우와 {items: [...]} 형태로 반환하는 경우 모두 처리
      if (result && result.data) {
        const { data } = result;
        let items;
        if (Array.isArray(data)) {
          // 배열이 직접 반환된 경우
          items = data;
        } else if (data.items) {
          // {items: [...]} 형태로 반환된 경우
          const { items: dataItems } = data;
          items = dataItems;
        } else {
          // 기타 예상치 못한 형식
          items = [];
        }

        this.setState({
          items,
          hasMore: items.length === limit,
        });
      } else {
        this.setState({ items: [], hasMore: false });
      }
    } catch (e) {
      console.log(e);
    }
  };

  componentDidMount() {
    this.initialize();
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  onClick = to => {
    this.props.history.push(to);
  };

  handleScroll = debounce(() => {
    const { loading } = this.props;
    const { hasMore, isSearching } = this.state;

    if (loading || !hasMore || isSearching) return;

    const { scrollHeight, clientHeight } = document.documentElement;
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;

    if (scrollTop + clientHeight >= scrollHeight - 300) {
      this.loadMore();
    }
  }, 200);

  loadMore = async () => {
    try {
      const { page, limit, sort, q, items } = this.state;
      const nextPage = page + 1;
      const result = await OrdersActions.listOrder({ page: nextPage, limit, sort, q });

      // API 응답 구조 처리
      if (result && result.data) {
        const { data } = result;
        let newItems = [];

        if (Array.isArray(data)) {
          // 배열이 직접 반환된 경우
          newItems = data;
        } else if (data.items) {
          // {items: [...]} 형태로 반환된 경우
          const { items: dataItems } = data;
          newItems = dataItems;
        }

        this.setState({
          page: nextPage,
          items: [...items, ...newItems],
          hasMore: newItems.length === limit,
        });
      } else {
        this.setState({ hasMore: false });
      }
    } catch (e) {
      console.log(e);
    }
  };

  // 검색어 입력 처리 - 상태만 업데이트하고 API 호출은 하지 않음
  handleSearchInputChange = q => {
    this.setState({ q });
  };

  // 검색 버튼 클릭 또는 엔터키 처리 - API 호출 실행
  handleSearchSubmit = async (searchQuery = null) => {
    // searchQuery가 전달되면 사용, 아니면 현재 상태의 q 사용
    const q = searchQuery !== null ? searchQuery : this.state.q;
    this.setState({ isSearching: true, page: 1 }); // 페이지를 1로 초기화
    try {
      const { limit, sort } = this.state;
      const result = await OrdersActions.listOrder({ page: 1, limit, sort, q }); // 페이지 1로 검색

      // API 응답 구조 처리
      if (result && result.data) {
        const { data } = result;
        let items = [];

        if (Array.isArray(data)) {
          // 배열이 직접 반환된 경우
          items = data;
        } else if (data.items) {
          // {items: [...]} 형태로 반환된 경우
          const { items: dataItems } = data;
          items = dataItems;
        }

        this.setState({
          items,
          hasMore: items.length === limit,
        });
      } else {
        this.setState({ items: [], hasMore: false });
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.setState({ isSearching: false });
    }
  };

  downloadRequestsExcel = async () => {
    const { data } = await OrdersActions.downloadRequestsExcel();
    this.download(data);
  };

  downloadInventoriesExcel = async () => {
    const { data } = await OrdersActions.downloadInventoriesExcel();
    this.download(data);
  };

  download = url => {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const name = new URL(url).pathname.split('/').pop();
        link.download = decodeURIComponent(name);
        link.click();
      })
      .catch(console.error);
  };

  handleAddToCart = async orderId => {
    try {
      const { items } = this.state;
      const order = items.find(item => item.id === orderId);

      if (!order || !order.items || order.items.length === 0) {
        BaseActions.showToast({
          type: 'error',
          message: '주문 정보를 찾을 수 없습니다.',
        });
        return;
      }

      const results = await Promise.allSettled(
        order.items.map(item =>
          CartActions.addItemCart({
            productId: item.product.id,
            productPriceId: item.product_price.id,
            quantity: item.quantity,
          }),
        ),
      );

      await CartActions.getCart();

      const successCount = results.filter(result => result.status === 'fulfilled').length;
      const failCount = results.filter(result => result.status === 'rejected').length;

      if (failCount === 0) {
        BaseActions.showToast({
          type: 'success',
          message: `${successCount}개 상품을 장바구니에 담았습니다.`,
        });
      } else {
        BaseActions.showToast({
          type: 'warning',
          message: `${successCount}개 상품 추가 성공, ${failCount}개 상품 추가 실패`,
        });
      }
    } catch (e) {
      console.error(e);
      BaseActions.showToast({
        type: 'error',
        message: '장바구니에 담는 중 오류가 발생했습니다.',
      });
    }
  };

  render() {
    const { loading, orders } = this.props;
    const { items, isSearching, q } = this.state;

    // 로딩 상태일 때 로딩 인디케이터 표시
    if (!items || (!items.length && (loading === undefined || loading)))
      return <Circles color="blue" size="medium" text="로딩 중..." />;

    // 백엔드 API 응답 구조에 맞게 데이터 전달
    // 백엔드는 배열을 직접 반환하므로 items 배열을 orders 객체의 items 속성으로 전달
    return (
      <OrderListTemplate
        orders={{ items }}
        onClick={this.onClick}
        downloadRequestsExcel={this.downloadRequestsExcel}
        downloadInventoriesExcel={this.downloadInventoriesExcel}
        onSearchInputChange={this.handleSearchInputChange}
        onSearchSubmit={this.handleSearchSubmit}
        onSearch={this.handleSearchSubmit} // 이전 버전과의 호환성 유지
        searchTerm={q}
        isSearching={isSearching}
        loading={loading}
        onAddToCart={this.handleAddToCart}
      />
    );
  }
}

export default connect(
  ({ orders, pender }: State) => ({
    orders,
    loading: pender.pending['orders/LIST_ORDER'],
  }),
  () => ({}),
)(withRouter(OrderListTemplateContainer));



// WEBPACK FOOTER //
// ./src/containers/order/OrderListTemplateContainer.js